/**
 * Marketo: Helpers
 *
 * Shared funcs that do common Marketo things. Primarily used to ensure Marketo
 * only loads forms once.
 *
 * Note: The module is called `MRKTO` rather than the more common `MKTO` to
 * differentiate between other Marketo stuff. You can remember it as "Mr. Katie Oh!"
 *
 * @example import { MRKTOHelpers } from '../common/marketo/mrkto-helpers.js';
 *
 * @module marketo/MRKTOHelpers
 */


// Data Store
// ============================================================================

const DATASTORE = {
	// Array of form IDs:
	// When a form is loaded, its ID gets added here, to ensure it won't be loaded a second time
	loadedFormIDs: [],

	// Object of form data objects (`LoadedFormData`), keyed by form ID
	loadedFormData: {
		// Example:
		// 123: { formID: 123, mktoFormObject: <object>, loadFormPromise: <promise>, callbacks: [<function>] }
	},
};


// ƒs
// ============================================================================

/**
 * Form data, stored in `DATASTORE.loadedFormData`
 *
 * @typedef {Object} LoadedFormData
 *
 * @property  {number}      formID           The form ID, same as in loadedFormIDs
 * @property  {object}      mktoFormObject   The Marketo form object that gets returned by `MktoForms2.loadForm`
 * @property  {Promise}     loadFormPromise  Promise that resolves when the form has loaded
 * @property  {function[]}  callbacks        Array of queued callback functions
 */


/**
 * Loads a Marketo form, but only once per form.
 *
 * This fixes an old bug in the legacy Marketo code (l-marketo.js) where having
 * multiple forms with the same ID would cause the form to be loaded multiple
 * times, causing each form container to render forms multiple times in a single
 * container.
 *
 * Uses promises to run the callbacks, which lets us only use `MktoForms2.loadForm`
 * once per form ID while still supporting the callbacks of any other forms using
 * that same ID.
 *
 * @link    https://developers.marketo.com/javascript-api/forms/api-reference/
 *
 * @param   {mixed}     formID    Marketo form ID (string/int)
 * @param   {function}  callback  Callback func to run after loading the form
 *
 * @return  {object}    Marketo form object. See the Marketo API docs for more info
 */
function loadMarketoFormOnce( formID, callback )
{
	// Checks if the provided form ID has already been used to load a form.
	// If so, loading the form is skipped, but the provided callback is added
	// to the callback queue, which will run when the original MktoForms2.loadForm
	// has finished (via `loadFormPromise.then`).
	if ( DATASTORE.loadedFormIDs.includes( formID ) )
	{
		// Queue up the callback. It will run when the form has loaded
		DATASTORE.loadedFormData[formID].callbacks.push( callback );

		return;
	}

	// Load the form. It's wrapped in a promise so we can run multiple callbacks
	// once the form has loaded and the promise has resolved
	const loadFormPromise = new Promise( ( resolve, reject ) =>
	{
		/**
		 * Loads a "form descriptor" from Marketo servers and creates a new Form object.
		 *
		 * If knows which form element to target based on the ID on the form,
		 * eg. "mktoForm_123". If you don't have a form with this ID, you'll need
		 * to render the form manually via `mktoFormObject.render(formElem)`,
		 * which can be done in the callback.
		 *
		 * See the linked docs for the available methods on the form object,
		 * mktoFormObject (look for "Form Methods").
		 *
		 * @example loadForm(baseUrl, munchkinId, formId, callback)
		 * @link    https://developers.marketo.com/javascript-api/forms/api-reference/
		 */
		MktoForms2.loadForm("https://communications.on24.com", "848-AHN-047", formID, ( mktoFormObject ) =>
		{
			DATASTORE.loadedFormData[formID].mktoFormObject = mktoFormObject;

			// Resolve the promise, returning the marketo form object
			resolve( mktoFormObject );
		});
	});

	// Store this form ID, so the form won't be loaded again
	DATASTORE.loadedFormIDs.push( formID );

	/**
	 * @type {LoadedFormData}
	 */
	const loadedFormData = {
		'formID':          formID,
		'mktoFormObject':  null, // will get updated once the form has loaded
		'loadFormPromise': loadFormPromise,
		'callbacks':       [],
	}

	// Store all the form data, mainly so we can add to the callback queue
	DATASTORE.loadedFormData[formID] = loadedFormData;

	// Add the callback for this initial form
	DATASTORE.loadedFormData[formID].callbacks.push( callback );

	// Stuff to do after the form is loaded, eg. run all registered callbacks
	loadFormPromise.then( ( mktoFormObject ) =>
	{
		// Run all queued callbacks.
		// This will run the original callback for the first instance of the
		// form that uses this func, followed by each other callback, in the
		// order they were added to the queue
		DATASTORE.loadedFormData[formID].callbacks.forEach( cb =>
		{
			cb( mktoFormObject );
		});
	});
}


// Exports
// ============================================================================

export const MRKTOHelpers = { loadMarketoFormOnce };
